export interface TimezoneOption {
  value: string;
  label: string;
}

const FALLBACK_TIMEZONES = [
  "UTC",
  "America/New_York",
  "America/Chicago",
  "America/Denver",
  "America/Los_Angeles",
  "America/Sao_Paulo",
  "Europe/London",
  "Europe/Paris",
  "Europe/Berlin",
  "Asia/Dubai",
  "Asia/Kolkata",
  "Asia/Manila",
  "Asia/Singapore",
  "Asia/Tokyo",
  "Australia/Sydney",
] as const;

let cachedOptions: TimezoneOption[] | null = null;
const intlWithSupportedValues = Intl as typeof Intl & {
  supportedValuesOf?: (key: "timeZone") => string[];
};

function getOffsetLabel(timezone: string): string {
  try {
    const parts = new Intl.DateTimeFormat("en-US", {
      timeZone: timezone,
      timeZoneName: "shortOffset",
      hour: "2-digit",
      minute: "2-digit",
    }).formatToParts(new Date());

    const offset = parts.find((part) => part.type === "timeZoneName")?.value;
    return offset ? offset.replace("GMT", "UTC") : "UTC";
  } catch {
    return "UTC";
  }
}

function buildLabel(timezone: string): string {
  const name = timezone.replace(/_/g, " ");
  return `(${getOffsetLabel(timezone)}) ${name}`;
}

function getSupportedTimezones(): string[] {
  try {
    if (typeof intlWithSupportedValues.supportedValuesOf === "function") {
      return intlWithSupportedValues.supportedValuesOf("timeZone");
    }
  } catch {
    // ignore
  }

  return [...FALLBACK_TIMEZONES];
}

export function getTimezoneOptions(): TimezoneOption[] {
  if (cachedOptions) {
    return cachedOptions;
  }

  cachedOptions = getSupportedTimezones()
    .filter((timezone) => typeof timezone === "string" && timezone.trim().length > 0)
    .map((timezone) => ({
      value: timezone,
      label: buildLabel(timezone),
    }))
    .sort((left, right) => left.label.localeCompare(right.label));

  return cachedOptions;
}
